import { Box, CircularProgress } from '@mui/material'
import { L } from '../ui/LavDropdown'

/**
 * Presentational primitives for the desktop "Settings · workspace" design
 * (Claude Design Settings.html spec). Purely visual — all state and handlers
 * stay in the Settings page; these just render the panels/fields/save-bar in
 * the lavender design language.
 */

// ---- icon set (design glyphs) ----
const PATHS = {
	chev: <path d="M4 6l4 4 4-4" />,
	plus: <path d="M8 3v10M3 8h10" />,
	trash: <path d="M3 4.5h10M6.5 4V3h3v1M5 4.5l.5 8h5l.5-8" />,
	x: <path d="M4 4l8 8M12 4l-8 8" />,
	copy: (
		<>
			<rect x="5.6" y="5.6" width="7.9" height="7.9" rx="1.3" />
			<path d="M3 10.4V4a1.5 1.5 0 011.5-1.5h6.4" />
		</>
	),
	check: <path d="M3 8.5l3.5 3.5L13 4.5" />,
	info: (
		<>
			<circle cx="8" cy="8" r="6.5" />
			<path d="M8 7.2v3.4" />
			<circle cx="8" cy="5" r="0.6" fill="currentColor" stroke="none" />
		</>
	),
	warn: (
		<>
			<path d="M8 2.2L14.6 13H1.4L8 2.2z" />
			<path d="M8 6.6v3" />
			<circle cx="8" cy="11.4" r="0.55" fill="currentColor" stroke="none" />
		</>
	),
	building: (
		<>
			<rect x="3" y="2.5" width="10" height="11" rx="1" />
			<path d="M5.5 5h2M8.5 5h2M5.5 7.5h2M8.5 7.5h2M5.5 10h2M8.5 10h2" />
		</>
	),
	bell: (
		<>
			<path d="M4.5 7a3.5 3.5 0 017 0c0 3 1.2 4 1.2 4H3.3S4.5 10 4.5 7z" />
			<path d="M6.7 13a1.4 1.4 0 002.6 0" />
		</>
	),
	shield: <path d="M8 2l5 2v4c0 3-2.2 5-5 6-2.8-1-5-3-5-6V4l5-2z" />,
	card: (
		<>
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M1.5 6.5h13" />
		</>
	),
	message: (
		<>
			<path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
		</>
	),
	clock: (
		<>
			<circle cx="8" cy="8" r="6" />
			<path d="M8 4.8V8l2.2 1.4" />
		</>
	),
	calendar: (
		<>
			<rect x="2.5" y="3" width="11" height="10.5" rx="1.5" />
			<path d="M2.5 6h11M5.5 1.8v2.2M10.5 1.8v2.2" />
		</>
	),
	phone: (
		<>
			<rect x="4.5" y="1.5" width="7" height="13" rx="1.5" />
			<path d="M7 12.5h2" />
		</>
	),
	terminal: (
		<>
			<rect x="3.5" y="1.5" width="9" height="13" rx="1.5" />
			<path d="M5.5 4h5v3h-5z" />
		</>
	),
	link: (
		<>
			<path d="M6.5 9.5l3-3" />
			<path d="M8.5 4.5l1-1a2.1 2.1 0 013 3l-1.6 1.6" />
			<path d="M7.5 11.5l-1 1a2.1 2.1 0 01-3-3l1.6-1.6" />
		</>
	),
	down: <path d="M4 6l4 4 4-4" />,
	back: <path d="M10 3.5L5.5 8l4.5 4.5" />,
	chevright: <path d="M6 3.5L10.5 8L6 12.5" />,
	mail: (
		<>
			<rect x="2" y="3.5" width="12" height="9" rx="1.5" />
			<path d="M2.5 4.5l5.5 4 5.5-4" />
		</>
	),
	eye: (
		<>
			<path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" />
			<circle cx="8" cy="8" r="2" />
		</>
	),
	edit: <path d="M11.5 2.5l2 2L6 12l-2.6.6L4 10z" />,
	camera: (
		<>
			<path d="M2.5 5.5h2.3l1.2-1.8h4l1.2 1.8h2.3v7.5h-11z" />
			<circle cx="8" cy="9" r="2.3" />
		</>
	),
	upload: <path d="M8 11V3.5M4.8 6.7L8 3.5l3.2 3.2M3 12.5h10" />,
	list: <path d="M5.5 4.5h8M5.5 8h8M5.5 11.5h8M2.8 4.5h.01M2.8 8h.01M2.8 11.5h.01" />,
}

export function StIc({ name, size = 16, sw = 1.6 }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none', display: 'block' }}
		>
			{PATHS[name] || null}
		</svg>
	)
}

// ---- MUI restyle: makes TextField / Select / pickers read as design .st-input ----
export const stInputSx = {
	'& .MuiOutlinedInput-root': {
		minHeight: 40, borderRadius: '9px', bgcolor: '#FFFFFF', fontFamily: L.font,
		fontSize: '13.5px', fontWeight: 500, color: L.text,
		'& fieldset': { borderColor: L.border, borderWidth: '1.5px' },
		'&:hover fieldset': { borderColor: L.primary },
		'&.Mui-focused fieldset': { borderColor: L.primary, borderWidth: '1.5px' },
	},
	'& .MuiOutlinedInput-input': {
		padding: '9px 12px', fontFamily: L.font, fontSize: '13.5px', color: L.text,
		'&::placeholder': { color: '#B9B2CC', opacity: 1 },
	},
	'& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
	'& .MuiFormHelperText-root': {
		fontFamily: L.font, fontSize: '11.5px', fontWeight: 600, color: L.muted, ml: '2px', mt: '5px',
	},
}

export const stMenuProps = {
	PaperProps: {
		sx: {
			borderRadius: '12px', border: `1px solid ${L.border}`, mt: '4px',
			boxShadow: '0 14px 36px rgba(95,78,194,0.16), 0 3px 10px rgba(95,78,194,0.08)',
			'& .MuiMenuItem-root': {
				fontFamily: L.font, fontSize: '13.5px', fontWeight: 600, color: L.text,
				borderRadius: '8px', mx: '5px', my: '1px',
				'&:hover': { bgcolor: L.surface },
				'&.Mui-selected': { bgcolor: L.primarySoft, color: L.primaryDeep, '&:hover': { bgcolor: L.primarySoft } },
			},
		},
	},
}

// ---- panel (st-panel) ----
export function StPanel({ title, subtitle, action, flush = false, children, sx = {} }) {
	return (
		<Box component="section" sx={{
			bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '14px',
			boxShadow: '0 1px 3px rgba(46,42,64,0.04)', fontFamily: L.font, ...sx,
		}}>
			{title && (
				<Box sx={{
					display: 'flex', alignItems: 'center', gap: '12px', p: '16px 20px',
					borderBottom: '1px solid #EFEBF7',
				}}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box sx={{ fontSize: '14.5px', fontWeight: 750, letterSpacing: '-0.01em', color: L.text }}>
							{title}
						</Box>
						{subtitle && (
							<Box sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted, mt: '2px' }}>
								{subtitle}
							</Box>
						)}
					</Box>
					{action}
				</Box>
			)}
			<Box sx={flush ? { p: 0 } : { p: '20px' }}>{children}</Box>
		</Box>
	)
}

// ---- field with label above (st-field) ----
export function StField({ label, hint, required, span2 = false, children }) {
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, ...(span2 ? { gridColumn: 'span 2' } : {}) }}>
			{label && (
				<Box component="span" sx={{ fontSize: '12px', fontWeight: 650, color: L.text }}>
					{label}{required && <Box component="em" sx={{ color: '#D96A6A', fontStyle: 'normal', fontWeight: 750 }}> *</Box>}
				</Box>
			)}
			{children}
			{hint && (
				<Box component="span" sx={{ fontSize: '11.5px', fontWeight: 600, color: L.muted, lineHeight: 1.45 }}>
					{hint}
				</Box>
			)}
		</Box>
	)
}

export const StGrid2 = ({ children, stacked = false, sx = {} }) => (
	<Box sx={{ display: 'grid', gridTemplateColumns: stacked ? '1fr' : '1fr 1fr', gap: '16px', ...sx }}>{children}</Box>
)

// ---- toggle (st-toggle) ----
export function StToggle({ on, onChange, disabled }) {
	return (
		<Box
			component="button"
			type="button"
			role="switch"
			aria-checked={on}
			disabled={disabled}
			onClick={onChange}
			sx={{
				width: 46, height: 26, flex: 'none', border: 'none', borderRadius: '99px',
				position: 'relative', p: 0, cursor: 'pointer', transition: 'background .15s',
				bgcolor: on ? L.primary : '#E0DAEF',
				'&:disabled': { opacity: 0.45, cursor: 'default' },
			}}
		>
			<Box sx={{
				position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20,
				borderRadius: '50%', bgcolor: '#FFFFFF', boxShadow: '0 1px 3px rgba(46,42,64,0.3)',
				transition: 'left .15s',
			}} />
		</Box>
	)
}

// ---- segmented control (st-seg) ----
export function StSeg({ value, options, onChange }) {
	return (
		<Box sx={{
			display: 'inline-flex', gap: '3px', bgcolor: L.surface, border: `1px solid ${L.border}`,
			borderRadius: '10px', p: '3px',
		}}>
			{options.map((o) => {
				const on = value === o.value
				return (
					<Box
						component="button"
						type="button"
						key={o.value}
						onClick={() => onChange(o.value)}
						sx={{
							border: 'none', borderRadius: '8px', p: '7px 14px', bgcolor: on ? '#FFFFFF' : 'transparent',
							fontSize: '13px', fontWeight: on ? 700 : 650, color: on ? L.primaryDeep : L.muted,
							fontFamily: L.font, whiteSpace: 'nowrap', cursor: 'pointer',
							boxShadow: on ? '0 1px 3px rgba(46,42,64,0.1)' : 'none',
							'&:hover': { color: on ? L.primaryDeep : L.text },
						}}
					>
						{o.label}
					</Box>
				)
			})}
		</Box>
	)
}

// ---- inline note (st-note) ----
export function StNote({ variant = 'info', children }) {
	const palette = variant === 'warn'
		? { bg: '#FDF3E4', color: '#8F6217' }
		: { bg: L.primarySoft, color: L.primaryDeep }
	return (
		<Box sx={{
			display: 'flex', alignItems: 'flex-start', gap: '9px', mt: '14px', p: '11px 13px',
			borderRadius: '10px', fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5,
			bgcolor: palette.bg, color: palette.color,
		}}>
			<Box sx={{ mt: '1px', flex: 'none', display: 'flex' }}><StIc name={variant === 'warn' ? 'warn' : 'info'} size={15} /></Box>
			<Box sx={{ minWidth: 0 }}>{children}</Box>
		</Box>
	)
}

// ---- status pill (st-statuspill) ----
export function StStatusPill({ ok, children }) {
	return (
		<Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: '6px', height: 26, pl: '9px', pr: '11px',
			borderRadius: '99px', fontSize: '12px', fontWeight: 700, flex: 'none',
			bgcolor: ok ? '#E9F6EE' : '#F0EDF5', color: ok ? '#25714A' : '#6E6889',
		}}>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ok ? '#2F8A5B' : '#A79FBE' }} />
			{children}
		</Box>
	)
}

// ---- buttons ----
export const stPrimaryBtnSx = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: 40, px: '16px',
	border: 'none', borderRadius: '10px', bgcolor: L.primary, color: L.onPrimary, fontSize: '13.5px',
	fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)', fontFamily: L.font, cursor: 'pointer',
	whiteSpace: 'nowrap',
	'&:hover': { bgcolor: L.primaryDeep },
	'&:disabled': { bgcolor: '#C9C2E4', boxShadow: 'none', cursor: 'default' },
}

export const stGhostBtnSx = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: 34, px: '13px',
	border: `1.5px solid ${L.border}`, borderRadius: '9px', bgcolor: '#FFFFFF', color: L.text,
	fontSize: '12.5px', fontWeight: 650, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: L.font,
	'&:hover:not(:disabled)': { borderColor: L.primary, color: L.primaryDeep },
	'&:disabled': { opacity: 0.55, cursor: 'default' },
}

export const stDangerBtnSx = {
	display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', height: 40, px: '16px',
	border: '1.5px solid #F2C3C0', borderRadius: '9px', bgcolor: '#FFFFFF', color: '#BE4B45',
	fontSize: '13px', fontWeight: 650, cursor: 'pointer', fontFamily: L.font, whiteSpace: 'nowrap',
	'&:hover:not(:disabled)': { bgcolor: '#FBECEB', borderColor: '#BE4B45' },
	'&:disabled': { opacity: 0.55, cursor: 'default' },
}

// ---- sub-tab pills (st-tabs) ----
export function StTabs({ tabs, active, onChange }) {
	return (
		<Box sx={{
			display: 'inline-flex', gap: '3px', bgcolor: '#FFFFFF', border: `1px solid ${L.border}`,
			borderRadius: '11px', p: '4px', flexWrap: 'wrap',
			// The header is a flex column, which blockifies `inline-flex` and
			// stretches it to full width — hug the tabs instead.
			alignSelf: 'flex-start', maxWidth: '100%',
		}}>
			{tabs.map((t) => {
				const on = active === t.id
				return (
					<Box
						component="button"
						type="button"
						key={t.id}
						onClick={() => onChange(t.id)}
						sx={{
							display: 'inline-flex', alignItems: 'center', gap: '8px', border: 'none',
							borderRadius: '8px', p: '8px 18px', bgcolor: on ? L.primarySoft : 'transparent',
							fontSize: '13.5px', fontWeight: on ? 750 : 650, color: on ? L.primaryDeep : L.muted,
							fontFamily: L.font, cursor: 'pointer',
							'&:hover': { color: on ? L.primaryDeep : L.text },
						}}
					>
						{t.icon && <StIc name={t.icon} size={15} />}
						<span>{t.label}</span>
					</Box>
				)
			})}
		</Box>
	)
}

// ---- sticky save bar (st-savebar) ----
export function StSaveBar({ saving, onDiscard, onSave, saveDisabled, saveLabel = 'Save changes' }) {
	return (
		<Box sx={{
			position: 'sticky', bottom: 16, zIndex: 20, display: 'flex', alignItems: 'center', gap: '12px',
			mt: '16px', p: '10px 12px 10px 18px', borderRadius: '13px', bgcolor: '#2E2A40', color: '#FFFFFF',
			boxShadow: '0 10px 30px rgba(46,42,64,0.35)', fontFamily: L.font,
		}}>
			<Box sx={{ width: 8, height: 8, flex: 'none', borderRadius: '50%', bgcolor: '#F0B44E' }} />
			<Box sx={{ flex: 1, fontSize: '13px', fontWeight: 650 }}>Unsaved changes</Box>
			<Box
				component="button"
				type="button"
				onClick={onDiscard}
				disabled={saving}
				sx={{
					display: 'inline-flex', alignItems: 'center', height: 36, px: '15px', border: 'none',
					borderRadius: '9px', bgcolor: 'transparent', color: '#C9C3DD', fontSize: '13px',
					fontWeight: 700, fontFamily: L.font, cursor: 'pointer',
					'&:hover:not(:disabled)': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' },
					'&:disabled': { opacity: 0.6, cursor: 'default' },
				}}
			>
				Discard
			</Box>
			<Box
				component="button"
				type="button"
				onClick={onSave}
				disabled={saving || saveDisabled}
				sx={{
					display: 'inline-flex', alignItems: 'center', gap: '7px', height: 36, px: '15px', border: 'none',
					borderRadius: '9px', bgcolor: L.primary, color: '#FFFFFF', fontSize: '13px', fontWeight: 700,
					fontFamily: L.font, cursor: 'pointer',
					'&:hover:not(:disabled)': { bgcolor: '#8D7DE2' },
					'&:disabled': { opacity: 0.6, cursor: 'default' },
				}}
			>
				{saving && <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />}
				<span>{saving ? 'Saving…' : saveLabel}</span>
			</Box>
		</Box>
	)
}
